// src/services/imoveisService.js
import axios from 'axios';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  getDoc,
  deleteDoc,
  serverTimestamp,
  addDoc,
  Timestamp,
  runTransaction,
} from 'firebase/firestore';
import { db, auth, storage } from './firebase';
import { getIdToken } from 'firebase/auth';
import { ref, deleteObject } from 'firebase/storage';
import { montarQueryImoveisPorOwner } from './queryBuilder';
import { toSlug } from '../utils/text';

// ✅ Score Events
import { EVENT_TYPES } from './leadershipEngine/score.config';

const API_URL = 'https://us-central1-elioncadastro.cloudfunctions.net/api';
const PAGE_LIMIT = 24;

/* ===========================
   HELPERS — SCORE EVENT
=========================== */
async function registrarEventoScore({ uid, tipo, refId = null }) {
  try {
    if (!uid || !tipo) return;
    await addDoc(collection(db, 'eventos_score'), {
      uid,
      tipo,
      refId: refId || null,
      createdAt: serverTimestamp(),
    });
  } catch (err) {
    console.warn(
      '[ScoreEvent] Falha ao registrar evento:',
      tipo,
      err?.message || err
    );
  }
}

/* ===========================
   ✅ DESTAQUES (ETAPA 05)
   - salva o imovelId em users/{uid}.siteBuilder.conteudo.(imoveis|destaques).ids
   - limite padrão 6
=========================== */
export const FEATURED_LIMIT_DEFAULT = 6;

function isObj(v) {
  return v && typeof v === 'object' && !Array.isArray(v);
}

function resolveModelId(raw) {
  const v = String(raw || '').toLowerCase();
  if (v.includes('imobili')) return 'imobiliaria';
  return 'corretor';
}

function getFeaturedIdsPath(modelId) {
  return resolveModelId(modelId) === 'imobiliaria'
    ? ['siteBuilder', 'conteudo', 'destaques', 'ids']
    : ['siteBuilder', 'conteudo', 'imoveis', 'ids'];
}

function getNestedArray(obj, path) {
  let cur = obj || {};
  for (const k of path) cur = cur?.[k];
  return Array.isArray(cur) ? cur : [];
}

function setNested(obj, path, value) {
  const out = { ...(obj || {}) };
  let cur = out;
  for (let i = 0; i < path.length - 1; i++) {
    const k = path[i];
    cur[k] = isObj(cur[k]) ? { ...cur[k] } : {};
    cur = cur[k];
  }
  cur[path[path.length - 1]] = value;
  return out;
}

/**
 * ✅ Lê os IDs em destaque salvos em users/{uid}.siteBuilder...
 * - corretor/lovable: siteBuilder.conteudo.imoveis.ids
 * - imobiliaria:      siteBuilder.conteudo.destaques.ids
 */
export async function getDestaquesIdsDoUsuario(uid) {
  if (!uid) return [];

  const userRef = doc(db, 'users', uid);
  const snap = await getDoc(userRef);
  if (!snap.exists()) return [];

  const user = snap.data() || {};
  const modelId = resolveModelId(
    user?.siteBuilder?.modelId ||
      user?.siteConfig?.modelId ||
      user?.modelId ||
      'corretor'
  );

  const path = getFeaturedIdsPath(modelId);
  return getNestedArray(user, path)
    .filter(Boolean)
    .map((x) => String(x));
}

/**
 * ✅ Toggle destaque do imóvel (ETAPA 05)
 */
export const toggleDestaqueImovel = async (
  imovelId,
  novoValor,
  uid,
  limiteMax = FEATURED_LIMIT_DEFAULT
) => {
  if (!imovelId) throw new Error('ID do imóvel ausente.');
  if (!uid) throw new Error('Usuário não autenticado.');

  const userRef = doc(db, 'users', uid);
  const idStr = String(imovelId);

  return runTransaction(db, async (tx) => {
    const userSnap = await tx.get(userRef);
    if (!userSnap.exists()) throw new Error('Usuário não encontrado.');

    const user = userSnap.data() || {};
    const modelId = resolveModelId(
      user?.siteBuilder?.modelId ||
        user?.siteConfig?.modelId ||
        user?.modelId ||
        'corretor'
    );

    const path = getFeaturedIdsPath(modelId);
    const current = getNestedArray(user, path)
      .filter(Boolean)
      .map((x) => String(x));

    const has = current.includes(idStr);

    // adicionar
    if (!!novoValor) {
      if (has) return { ok: true, modelId, ids: current };
      if (current.length >= limiteMax) {
        throw new Error(
          `Limite de ${limiteMax} imóveis em destaque atingido. Remova um para adicionar outro.`
        );
      }
      const next = [idStr, ...current].slice(0, limiteMax);
      tx.set(userRef, setNested({}, path, next), { merge: true });
      tx.set(userRef, { updatedAt: serverTimestamp() }, { merge: true });
      return { ok: true, modelId, ids: next };
    }

    // remover
    if (!has) return { ok: true, modelId, ids: current };

    const next = current.filter((x) => x !== idStr);
    tx.set(userRef, setNested({}, path, next), { merge: true });
    tx.set(userRef, { updatedAt: serverTimestamp() }, { merge: true });
    return { ok: true, modelId, ids: next };
  });
};

/* ===========================
   HELPERS — PAYLOAD
=========================== */

function normalizeTitulo(dados) {
  const t = String(dados?.titulo || '').trim();
  if (t) return t;
  const b = String(dados?.bairro || '').trim();
  return b;
}

function normalizeTemPlaca(dados) {
  return typeof dados?.temPlaca === 'boolean'
    ? dados.temPlaca
    : !!dados?.temPlaca;
}

function pickOwnerFields(currentUser) {
  return {
    ownerId: currentUser.uid,
    ownerNome: currentUser.displayName || '',
    ownerEmail: currentUser.email || '',
    usuario: {
      uid: currentUser.uid,
      nome: currentUser.displayName || '',
      email: currentUser.email || '',
    },
    usuarioId: currentUser.uid,
  };
}

function enrichSlugs(dados) {
  return {
    estadoSlug: toSlug(dados?.estado || ''),
    cidadeSlug: toSlug(dados?.cidade || ''),
    bairroSlug: dados?.bairro ? toSlug(dados.bairro) : null,
  };
}

/* -------------------- CADASTRAR -------------------- */
export const cadastrarImovel = async (dados) => {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error('Usuário não autenticado.');
  const token = await getIdToken(currentUser);

  const payload = {
    ...dados,
    ...pickOwnerFields(currentUser),
    ...enrichSlugs(dados),
    titulo: normalizeTitulo(dados),
    status: dados?.status || 'publicado',
    statusNegocio: dados?.statusNegocio || 'ativo',
    temPlaca: normalizeTemPlaca(dados),
    referencia: dados?.referencia || dados?.refCodigo || '',
  };

  const resposta = await axios.post(`${API_URL}/cadastrar-imovel`, payload, {
    headers: { Authorization: `Bearer ${token}` },
  });

  const imovelId =
    resposta?.data?.id ||
    resposta?.data?.imovelId ||
    resposta?.data?.docId ||
    null;

  await registrarEventoScore({
    uid: currentUser.uid,
    tipo: EVENT_TYPES.IMOVEL_CADASTRADO,
    refId: imovelId,
  });

  return resposta.data;
};

/* -------------------- EDITAR / STATUS / EXCLUSÃO -------------------- */
export const editarImovel = async (id, dadosAtualizados) => {
  const imovelRef = doc(db, 'imoveis', id);

  const patch = {
    ...dadosAtualizados,
    ...(dadosAtualizados.estado !== undefined
      ? { estadoSlug: toSlug(dadosAtualizados.estado || '') }
      : {}),
    ...(dadosAtualizados.cidade !== undefined
      ? { cidadeSlug: toSlug(dadosAtualizados.cidade || '') }
      : {}),
    ...(dadosAtualizados.bairro !== undefined
      ? {
          bairroSlug: dadosAtualizados.bairro
            ? toSlug(dadosAtualizados.bairro)
            : null,
        }
      : {}),
    atualizadoEm: serverTimestamp(),
  };

  if (dadosAtualizados?.refCodigo !== undefined && !patch.referencia) {
    patch.referencia = dadosAtualizados.refCodigo || '';
  }

  await updateDoc(imovelRef, patch);
  return { id, ...dadosAtualizados };
};

/**
 * ✅ Toggle placa do imóvel (salva automático)
 */
export const togglePlacaImovel = async (imovelId, novoValor) => {
  const refDoc = doc(db, 'imoveis', imovelId);
  await updateDoc(refDoc, {
    temPlaca: !!novoValor,
    atualizadoEm: serverTimestamp(),
  });
};

/**
 * ✅ Atualizar status do NEGÓCIO do imóvel (funil)
 */
export const atualizarStatusImovel = async (
  id,
  novoStatusNegocio,
  vendaInfo = null
) => {
  const refDoc = doc(db, 'imoveis', id);
  await updateDoc(refDoc, {
    statusNegocio: novoStatusNegocio,
    vendaInfo: vendaInfo ?? null,
    atualizadoEm: serverTimestamp(),
  });
};

export const encerrarAnuncio = async (imovelId, { resultado, venda }) => {
  if (!imovelId) throw new Error('ID do imóvel é obrigatório.');
  if (!resultado) throw new Error('Resultado do encerramento é obrigatório.');

  const imovelRef = doc(db, 'imoveis', imovelId);
  const snap = await getDoc(imovelRef);

  if (!snap.exists()) {
    throw new Error('Imóvel não encontrado para encerrar anúncio.');
  }

  const dadosImovel = snap.data() || {};
  const ownerId =
    dadosImovel.ownerId ||
    dadosImovel.usuarioId ||
    dadosImovel.criadoPorUid ||
    dadosImovel.usuario?.uid ||
    dadosImovel.criadoPor?.uid ||
    null;

  const updatePayload = {
    statusNegocio: resultado,
    status: 'encerrado',
    atualizadoEm: serverTimestamp(),
  };

  if (resultado === 'vendido' && venda) {
    updatePayload.vendaInfo = {
      data: venda.data || null,
      clienteNome: venda.clienteNome || '',
      produtoDescricao: venda.produtoDescricao || '',
      valor: venda.valor || '',
      comissao: venda.comissao || '',
    };
  } else {
    updatePayload.vendaInfo = null;
  }

  await updateDoc(imovelRef, updatePayload);

  // ✅ registra venda
  if (resultado === 'vendido' && venda) {
    let dataVendaTs;
    if (venda.data) {
      const [ano, mes, dia] = String(venda.data)
        .split('-')
        .map((p) => Number(p));
      const d = new Date(ano, (mes || 1) - 1, dia || 1);
      dataVendaTs = Timestamp.fromDate(d);
    } else {
      dataVendaTs = serverTimestamp();
    }

    const valorNumber = Number(venda.valor || 0);
    const comissaoNumber = Number(venda.comissao || 0);

    const vendaRef = await addDoc(collection(db, 'vendas'), {
      imovelId,
      uid: ownerId || null,
      corretorId: ownerId || null,
      clienteNome: venda.clienteNome || '',
      produtoDescricao: venda.produtoDescricao || '',
      valor: Number.isFinite(valorNumber) ? valorNumber : 0,
      comissao: Number.isFinite(comissaoNumber) ? comissaoNumber : 0,
      data: dataVendaTs,
      criadoEm: serverTimestamp(),
    });

    await registrarEventoScore({
      uid: ownerId,
      tipo: EVENT_TYPES.VENDA_CADASTRADA,
      refId: vendaRef?.id || null,
    });
  }

  return { ok: true };
};

export const deleteImagemImovel = async (path) => {
  const imageRef = ref(storage, path);
  await deleteObject(imageRef);
};

export const excluirImovel = async (imovelId) => {
  const docRef = doc(db, 'imoveis', imovelId);
  const snapshot = await getDoc(docRef);
  if (!snapshot.exists()) throw new Error('Imóvel não encontrado.');

  const dados = snapshot.data() || {};
  if (Array.isArray(dados.imagens)) {
    for (const imagem of dados.imagens) {
      if (imagem?.path) {
        try {
          await deleteImagemImovel(imagem.path);
        } catch (_) {}
      }
    }
  }

  await deleteDoc(docRef);
};

export const getImovelPorId = async (id) => {
  const refDoc = doc(db, 'imoveis', id);
  const snapshot = await getDoc(refDoc);
  return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
};

/* --------------------
   PERMISSÕES
-------------------- */
export const getPermissoesUsuario = async (uid) => {
  if (!uid) return { ids: [], socios: [], parceiros: [] };

  const [socA, socB, parA, parB] = await Promise.all([
    getDocs(
      query(
        collection(db, 'socios'),
        where('status', '==', 'accepted'),
        where('criador', '==', uid)
      )
    ),
    getDocs(
      query(
        collection(db, 'socios'),
        where('status', '==', 'accepted'),
        where('convidado', '==', uid)
      )
    ),
    getDocs(
      query(
        collection(db, 'parcerias'),
        where('status', '==', 'accepted'),
        where('from', '==', uid)
      )
    ),
    getDocs(
      query(
        collection(db, 'parcerias'),
        where('status', '==', 'accepted'),
        where('to', '==', uid)
      )
    ),
  ]);

  const socios = [...socA.docs, ...socB.docs].map((d) =>
    d.data().criador === uid ? d.data().convidado : d.data().criador
  );
  const parceiros = [...parA.docs, ...parB.docs].map((d) =>
    d.data().from === uid ? d.data().to : d.data().from
  );

  return {
    ids: [...new Set([uid, ...socios, ...parceiros])],
    socios,
    parceiros,
  };
};

/* --------------------
   LISTAGEM (com fallback ownerId/usuarioId)
-------------------- */
export const getImoveis = async (userId, filtros = {}, ultimoCursor = null) => {
  const {
    ids: idsPermitidos,
    socios,
    parceiros,
  } = await getPermissoesUsuario(userId);

  if (!idsPermitidos.length)
    return { imoveis: [], ultimoDoc: null, temMais: false };

  const ownerIds = [];
  if (filtros.origemMeus) ownerIds.push(userId);
  if (filtros.origemSocios) ownerIds.push(...socios);
  if (filtros.origemParceiros) ownerIds.push(...parceiros);

  const ownerIdsUnicos = [...new Set(ownerIds)];
  if (!ownerIdsUnicos.length)
    return { imoveis: [], ultimoDoc: null, temMais: false };

  const grupos =
    ownerIdsUnicos.length > 10
      ? Array.from({ length: Math.ceil(ownerIdsUnicos.length / 10) }, (_, i) =>
          ownerIdsUnicos.slice(i * 10, i * 10 + 10)
        )
      : [ownerIdsUnicos];

  const ownerFields = [
    'ownerId',
    'usuarioId',
    'uid',
    'criadoPorUid',
    'criadoPor.uid',
    // 'usuario.uid' // ⚠️ se der erro, deixe comentado
  ];

  const queriesArr = [];
  for (const g of grupos) {
    for (const field of ownerFields) {
      queriesArr.push(
        montarQueryImoveisPorOwner(field, filtros, g, {
          pageSize: PAGE_LIMIT,
          afterDoc: ultimoCursor,
        })
      );
    }
  }

  const snaps = await Promise.all(queriesArr.map((q) => getDocs(q)));

  const mapa = new Map();
  snaps.forEach((s) => s.docs.forEach((d) => mapa.set(d.id, d)));

  let items = Array.from(mapa.values()).map((d) => ({ id: d.id, ...d.data() }));

  // filtros extras client-side
  if (filtros.cidade) items = items.filter((i) => i.cidade === filtros.cidade);
  if (filtros.estado) items = items.filter((i) => i.estado === filtros.estado);
  if (filtros.bairro) {
    const termo = String(filtros.bairro).toLowerCase();
    items = items.filter((i) => (i.bairro || '').toLowerCase().includes(termo));
  }
  if (filtros.codigo?.trim()) {
    const termo = filtros.codigo.trim().toUpperCase();
    items = items.filter(
      (i) =>
        (i.referencia || '').toUpperCase() === termo ||
        (i.refCodigo || '').toUpperCase() === termo
    );
  }
  if (filtros.tipo && filtros.tipo !== 'Todos') {
    items = items.filter((i) => i.tipo === filtros.tipo);
  }

  // origem
  items = items.map((i) => {
    const dono =
      i.ownerId ||
      i.usuarioId ||
      i.criadoPorUid ||
      i.usuario?.uid ||
      i.criadoPor?.uid ||
      '';
    let origem = 'parceiro';
    if (dono === userId) origem = 'meu';
    else if (socios.includes(dono)) origem = 'socio';
    return { ...i, origem };
  });

  // ordenação robusta
  const toMs = (ts) =>
    ts?.toMillis ? ts.toMillis() : ts?.seconds ? ts.seconds * 1000 : 0;

  items.sort(
    (a, b) => toMs(b.criadoEm || b.createdAt) - toMs(a.criadoEm || a.createdAt)
  );

  const last = items[items.length - 1];
  const ultimoDoc = last
    ? { id: last.id, criadoEm: last.criadoEm || last.createdAt }
    : null;

  const temMais = items.length >= PAGE_LIMIT;
  return { imoveis: items, ultimoDoc, temMais };
};

export async function listarImoveisVisiveisNaRede(
  uid,
  filtrosExtra = {},
  ultimoCursor = null
) {
  return getImoveis(
    uid,
    {
      origemMeus: true,
      origemSocios: true,
      origemParceiros: false,
      ...filtrosExtra,
    },
    ultimoCursor
  );
}
